// web-admin/src/pages/ApprovalCenter.js
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Button,
  Drawer,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Stack,
  Fade,
  InputAdornment,
  Card
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

import {
  Close as CloseIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AssignmentTurnedIn as ApprovalIcon,
  AttachFile as AttachmentIcon
} from "@mui/icons-material";

import AppHeader from "../components/AppHeader";
import { formatDDMMYYYY, formatYangon } from "../services/datetimeService";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

ModuleRegistry.registerModules([AllCommunityModule]);

export default function ApprovalCenter({ setIsLoggedIn }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [otApprovedHours, setOtApprovedHours] = useState("");

  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [rejectDialog, setRejectDialog] = useState({ open: false, item: null, fromDrawer: false, reason: "" });

  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoTitle, setPhotoTitle] = useState("");

  const gridRef = useRef(null);
  const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user")) || null;
    } catch (e) {
      return null;
    }
  }, []);

  const isSelectedOwnStep1Manual = useMemo(() => {
    return selected?.type === "manual_attendance" && selected?.staff_user_id === currentUser?.id && selected?.current_step === 1;
  }, [selected, currentUser]);

  const level1Approver = selected?.level1_approver_name || selected?.manager_name || "-";
  const level1Status = selected?.level1_status || "Pending";
  const level1Time = selected?.level1_acted_at ? formatYangon(selected?.level1_acted_at) : "-";

  const getDisplayStatus = useCallback((row) => {
    if (!row) return "-";
    if (row.type === "ot_request" && row.status === "pending") {
      return Number(row.current_step || 1) > 1 ? "in_review" : "submitted";
    }
    return row.status;
  }, []);

  const formatLeavePortion = useCallback((row) => {
    if (!row) return "-";
    const totalDays = Number(row.total_days || 0);
    const halfDay = String(row.half_day_type || "none").toLowerCase();
    const startDay = String(row.start_day_type || "full").toLowerCase();
    const endDay = String(row.end_day_type || "full").toLowerCase();

    if (totalDays === 0.5 || halfDay !== "none") {
      return halfDay === "morning" ? "Half day - Morning" : halfDay === "afternoon" ? "Half day - Afternoon" : "Half day";
    }

    const parts = [];
    if (startDay === "afternoon") parts.push("Start PM");
    if (endDay === "morning") parts.push("End AM");
    return parts.length ? parts.join(" / ") : "Full day";
  }, []);

  const formatLeaveDateRange = useCallback((row) => {
    if (!row) return "-";
    const start = formatDDMMYYYY(row.start_date || row.date) || "-";
    const end = formatDDMMYYYY(row.end_date || row.date) || "-";
    return start === end ? start : `${start} to ${end}`;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL1}/approval/inbox`,
        {
          withCredentials: true,
          headers: { "X-Tenant": tenant }
        }
      );

      if (res.data.success) {
        setItems(res.data.items);
      }
    } catch (err) {
      console.error("Approval load error:", err);
      setSnack({ open: true, message: "Failed to load approval requests", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const viewPhoto = useCallback(async (row) => {
    setPhotoTitle(`Evidence - ${row.staff_name}`);
    setPhotoOpen(true);
    setPhotoLoading(true);
    setPhotoUrl(null);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL1}/admin/documents/${row.attachment_path}/preview`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: { "X-Tenant": tenant }
        }
      );
      const url = URL.createObjectURL(res.data);
      setPhotoUrl(url);
    } catch (err) {
      console.error("Photo load error:", err);
      setSnack({ open: true, message: "Failed to load document", severity: "error" });
    } finally {
      setPhotoLoading(false);
    }
  }, [tenant]);

  const handleClosePhoto = useCallback(() => {
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoOpen(false);
    setPhotoUrl(null);
  }, [photoUrl]);

  const openDetail = useCallback((row) => {
    setSelected(row);
    if (row.type === "ot_request") {
      const systemHours = Number(row.system_calculated_hours || 0);
      setOtApprovedHours(String(systemHours > 0 ? systemHours : (row.requested_hours ?? "")));
    }
    setDetailOpen(true);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setSelected(null);
  }, []);

  const requiresRejectReason = useCallback((type) => {
    return ["manual_attendance", "ot_request", "leave_request", "comp_off_request"].includes(type);
  }, []);

  const openRejectDialog = useCallback((item, fromDrawer = false) => {
    setRejectDialog({ open: true, item, fromDrawer, reason: "" });
  }, []);

  const closeRejectDialog = useCallback(() => {
    setRejectDialog({ open: false, item: null, fromDrawer: false, reason: "" });
  }, []);

  const handleAction = useCallback(async (id, type, action, fromDrawer = false, comment = "") => {
    try {
      const trimmedComment = String(comment || "").trim();
      if (action === "reject" && requiresRejectReason(type) && !trimmedComment) {
        setSnack({ open: true, message: "Rejection reason is required", severity: "error" });
        return;
      }

      const endpoint =
        type === "manual_attendance"
          ? `/approval/${action}`
          : type === "ot_request"
            ? `/overtime/${action}`
            : type === "leave_request"
              ? `/leave/admin/handle-action`
              : type === "comp_off_request"
                ? `/compoff/action`
                : action === "approve"
                  ? `/device/hr-approve-otp`
                  : `/device/hr-reject-otp`;

      let payload;
      if (type === "device_change") {
        payload = { otp_request_id: id, action };
      } else if (type === "ot_request") {
        payload = { id: id, action };
        if (action === "reject") {
          payload.comment = trimmedComment;
        }
        if (action === "approve") {
          if (fromDrawer && id === selected?.id) {
            payload.approved_hours = parseFloat(otApprovedHours);
          } else {
            const row = items.find(r => r.id === id && r.type === "ot_request");
            const systemHours = Number(row?.system_calculated_hours || 0);
            payload.approved_hours = systemHours > 0
              ? systemHours
              : Number(row?.requested_hours || 0);
          }
        }
      } else {
        payload = { request_id: id, action };
        if (action === "reject") {
          payload.comment = trimmedComment;
        }
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL1}${endpoint}`,
        payload,
        {
          withCredentials: true,
          headers: { "X-Tenant": tenant }
        }
      );

      if (res.data?.success === false) {
        setSnack({ open: true, message: res.data.message || "Action not allowed", severity: "error" });
        return;
      }
      
      setSnack({ open: true, message: `Request ${action}d successfully`, severity: "success" });
      loadData();
      if (fromDrawer) closeDetail();
      if (action === "reject") closeRejectDialog();
    } catch (err) {
      console.error("Action error:", err);
      setSnack({ open: true, message: err?.response?.data?.message || "Action failed", severity: "error" });
    }
  }, [tenant, items, selected, loadData, otApprovedHours, closeDetail, closeRejectDialog, requiresRejectReason]);

  const submitReject = useCallback(() => {
    if (!rejectDialog.item) return;
    handleAction(
      rejectDialog.item.id,
      rejectDialog.item.type,
      "reject",
      rejectDialog.fromDrawer,
      rejectDialog.reason
    );
  }, [rejectDialog, handleAction]);

  // --- Grid Cell Renderers ---
  const TypeBadge = useCallback((params) => {
    const type = params.value;
    const config = {
      manual_attendance: { label: "MANUAL ATTENDANCE", color: "#f59e0b", bg: "#fef3c7" },
      ot_request: { label: "OT REQUEST", color: "primary.main", bg: "#eef2ff" },
      leave_request: { label: "LEAVE REQUEST", color: "#10b981", bg: "#ecfdf5" },
      comp_off_request: { label: "COMP OFF", color: "#d946ef", bg: "#fdf4ff" },
      device_change: { label: "DEVICE CHANGE", color: "#8b5cf6", bg: "#f5f3ff" }
    }[type] || { label: type.toUpperCase(), color: "#64748b", bg: "#f1f5f9" };

    return (
      <Box sx={{
        display: "inline-flex", px: 1.2, py: 0.3, borderRadius: 1.5,
        bgcolor: config.bg, border: "1px solid", borderColor: `${config.color}30`,
        alignItems: "center", mt: 1.2
      }}>
        <Typography variant="caption" sx={{ color: config.color, fontWeight: 800, fontSize: 10 }}>
          {config.label}
        </Typography>
      </Box>
    );
  }, []);

  const StatusRenderer = useCallback((params) => {
    const displayStatus = getDisplayStatus(params.data);
    const isPending = ["pending", "submitted", "in_review"].includes(displayStatus);
    const color = isPending ? "#f59e0b" : displayStatus === "approved" ? "#10b981" : "#ef4444";
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Box sx={{
          width: 8, height: 8, borderRadius: "50%",
          bgcolor: color, mr: 1,
          boxShadow: isPending ? `0 0 0 2px ${color}20` : "none"
        }} />
        <Typography variant="body2" sx={{ color: color, fontWeight: 700, fontSize: "0.85rem", textTransform: "capitalize" }}>
          {String(displayStatus).replaceAll("_", " ")}
        </Typography>
      </Box>
    );
  }, [getDisplayStatus]);

  const ActionRenderer = useCallback((params) => {
    const r = params.data;
    const isOwnStep1ManualRequest = r.type === "manual_attendance" && r.staff_user_id === currentUser?.id && r.current_step === 1;

    return (
      <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
        {r.attachment_path && (
          <Tooltip title="View Evidence">
            <IconButton size="small" onClick={() => viewPhoto(r)} sx={{ color: "primary.main", "&:hover": { bgcolor: "#f5f3ff" } }}>
              <AttachmentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="View Details">
          <IconButton size="small" onClick={() => openDetail(r)} sx={{ color: "#64748b", "&:hover": { bgcolor: "#f1f5f9" } }}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={isOwnStep1ManualRequest ? "Self-approval is not allowed at the line manager step." : "Approve"}>
          <span>
            <IconButton 
              size="small" 
              onClick={() => handleAction(r.id, r.type, "approve")} 
              disabled={isOwnStep1ManualRequest}
              sx={{ color: "#10b981", "&:hover": { bgcolor: "#ecfdf5" } }}
            >
              <ApproveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isOwnStep1ManualRequest ? "Self-approval is not allowed at the line manager step." : "Reject"}>
          <span>
            <IconButton 
              size="small" 
              onClick={() => requiresRejectReason(r.type) ? openRejectDialog(r) : handleAction(r.id, r.type, "reject")}
              disabled={isOwnStep1ManualRequest}
              sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}
            >
              <RejectIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  }, [viewPhoto, openDetail, handleAction, openRejectDialog, requiresRejectReason, currentUser]);

  const columnDefs = useMemo(() => [
    {
      headerName: "Type",
      field: "type",
      width: 160,
      cellRenderer: TypeBadge
    },
    {
      headerName: "Staff Name",
      field: "staff_name",
      width: 200,
      cellStyle: { fontWeight: 700, color: "#1e293b" }
    },
    {
      headerName: "Request Date",
      field: "date",
      width: 140,
      valueFormatter: (p) => formatDDMMYYYY(p.value) || "-"
    },
    {
      headerName: "Reason",
      field: "reason",
      flex: 1,
      minWidth: 200,
      tooltipField: "reason"
    },
    {
      headerName: "Reviewer",
      field: "reviewed_by",
      width: 140,
      cellStyle: { fontWeight: 600, color: "#64748b" },
      valueFormatter: (p) => p.value || "-"
    },
    {
      headerName: "Submitted",
      field: "submitted_at",
      width: 160,
      valueFormatter: (p) => formatYangon(p.value)
    },
    {
      headerName: "Status",
      field: "status",
      width: 120,
      cellRenderer: StatusRenderer
    },
    {
      headerName: "Actions",
      width: 160,
      pinned: "right",
      cellRenderer: ActionRenderer,
      sortable: false,
      filter: false
    }
  ], [TypeBadge, StatusRenderer, ActionRenderer]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    headerClass: "premium-grid-header"
  }), []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", bgcolor: "#f8fafc" }}>
      <AppHeader setIsLoggedIn={setIsLoggedIn} />

      <Box sx={{ p: 3, flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column", gap: 2, maxWidth: 1400, mx: "auto", width: "100%" }}>
        
        <Fade in timeout={500}>
          <Card sx={{ p: 2, px: 3, borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.04)", bgcolor: "#fff" }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ p: 1, bgcolor: "#f1f5f9", borderRadius: 2.5, display: "flex" }}>
                    <ApprovalIcon sx={{ color: "primary.main", fontSize: 24 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                    Approval Center
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "#64748b", mt: 0.5, ml: 6, display: "block", fontSize: "0.85rem" }}>
                  Review and process pending staff requests and operational changes.
                </Typography>
              </Box>

              <Stack direction="row" spacing={2}>
                <TextField
                  size="small"
                  placeholder="Quick search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2.5, bgcolor: "#f1f5f9", border: "none", "& fieldset": { border: "none" } }
                  }}
                  sx={{ width: 320 }}
                />
                <Tooltip title="Refresh Inbox">
                  <IconButton onClick={loadData} sx={{ bgcolor: "#f1f5f9", borderRadius: 2.5 }}><RefreshIcon /></IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Card>
        </Fade>

        <Paper sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.03)", flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Box className="ag-theme-quartz" sx={{ flexGrow: 1, width: "100%", "& .premium-grid-header": { fontWeight: 800, color: "#475569" } }}>
            <AgGridReact
              ref={gridRef}
              rowData={items}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowHeight={52}
              headerHeight={48}
              animateRows={true}
              pagination={true}
              paginationPageSize={10}
              quickFilterText={searchText}
              rowSelection={{ mode: "singleRow" }}
              suppressCellFocus={true}
              overlayNoRowsTemplate={loading ? "Loading requests..." : "No pending approval requests found"}
            />
          </Box>
        </Paper>
      </Box>

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={closeDetail}
        PaperProps={{ sx: { width: 450, borderRadius: "16px 0 0 16px", boxShadow: -5 } }}
      >
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>Request Details</Typography>
            <IconButton onClick={closeDetail} size="small"><CloseIcon /></IconButton>
          </Stack>
          
          <Divider sx={{ mb: 3 }} />

          {selected && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flexGrow: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: "primary.main", fontWeight: 800 }}>{selected.staff_name?.charAt(0)}</Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b" }}>{selected.staff_name}</Typography>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>Submitted {formatYangon(selected.submitted_at)}</Typography>
                </Box>
              </Stack>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <DetailRow label="Type" value={<TypeBadge value={selected.type} />} />
                <DetailRow label={selected.type === "comp_off_request" ? "Worked Date" : "Requested Date"} value={formatDDMMYYYY(selected.date) || "-"} />
                {selected.type === "comp_off_request" && (
                  <DetailRow label="Substitute Off Date" value={formatDDMMYYYY(selected.substitute_off_date) || "-"} highlight />
                )}
                
                {selected.type === "manual_attendance" && (
                  <>
                    <DetailRow label="Requested In" value={selected.requested_clock_in_time ? formatYangon(selected.requested_clock_in_time) : "-"} highlight />
                    <DetailRow label="Requested Out" value={selected.requested_clock_out_time ? formatYangon(selected.requested_clock_out_time) : "-"} highlight />
                  </>
                )}

                {selected.type === "ot_request" && (
                  <>
                    <DetailRow label="Clock In" value={formatYangon(selected.clock_in_time) || "-"} />
                    <DetailRow label="Clock Out" value={formatYangon(selected.clock_out_time) || "-"} />
                    <DetailRow label="Requested OT" value={selected.requested_hours ? `${selected.requested_hours} hrs` : "-"} highlight />
                    {selected.system_calculated_hours !== undefined && (
                      <DetailRow label="System Calc. OT" value={`${selected.system_calculated_hours} hrs`} color="primary.main" />
                    )}
                  </>
                )}

                {selected.type === "leave_request" && (
                  <Box sx={{
                    p: 2,
                    bgcolor: "#f8fafc",
                    borderRadius: 3,
                    border: "1px solid #e2e8f0"
                  }}>
                    <Typography variant="caption" sx={{ color: "#475569", fontWeight: 800, textTransform: "uppercase", display: "block", mb: 1.5 }}>
                      Leave Details
                    </Typography>
                    <Stack spacing={1.5}>
                      <DetailRow label="Leave Type" value={selected.leave_type_name || selected.leave_type_code || "-"} />
                      <DetailRow label="Leave Date" value={formatLeaveDateRange(selected)} highlight />
                      <DetailRow label="Leave Portion" value={formatLeavePortion(selected)} color="#6366f1" />
                      <DetailRow label="Total Days" value={`${Number(selected.total_days || 0).toLocaleString()} day${Number(selected.total_days || 0) === 1 ? "" : "s"}`} color="#10b981" />
                      <DetailRow
                        label="Supporting Document"
                        value={selected.attachment_path ? "Available" : "Not attached"}
                        color={selected.attachment_path ? "#10b981" : "#64748b"}
                      />
                    </Stack>
                    {selected.attachment_path && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AttachmentIcon />}
                        onClick={() => viewPhoto(selected)}
                        sx={{ mt: 2, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                      >
                        View Supporting Document
                      </Button>
                    )}
                  </Box>
                )}

                <DetailRow label="Reviewer" value={selected.reviewed_by || "Not reviewed yet"} color={selected.reviewed_by ? "#10b981" : "#64748b"} />
                <DetailRow label="Status" value={<StatusRenderer value={selected.status} data={selected} />} />

                {(selected.type === "manual_attendance" || selected.type === "ot_request" || selected.type === "leave_request" || selected.type === "comp_off_request") && (
                  <Box sx={{ 
                    mt: 1.5, 
                    p: 2, 
                    bgcolor: level1Status === "approved" ? "#f0fdf4" : level1Status === "rejected" ? "#fef2f2" : "#f8fafc", 
                    borderRadius: 3, 
                    border: "1px dashed", 
                    borderColor: level1Status === "approved" ? "#bbf7d0" : level1Status === "rejected" ? "#fecaca" : "#e2e8f0" 
                  }}>
                    <Typography variant="caption" sx={{ color: "#475569", fontWeight: 800, textTransform: "uppercase", display: "block", mb: 1.5 }}>
                      Level 1 Approval Details
                    </Typography>
                    <Stack spacing={1.5}>
                      <DetailRow label="Level 1 Approver" value={level1Approver} />
                      <DetailRow 
                        label="Reviewed Status" 
                        value={level1Status.toUpperCase()} 
                        color={level1Status === "approved" ? "#10b981" : level1Status === "rejected" ? "#ef4444" : "#f59e0b"} 
                      />
                      <DetailRow label="Reviewed Time" value={level1Time} />
                    </Stack>
                  </Box>
                )}
                
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Reason / Remarks</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: "#f8fafc", borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{selected.type === "leave_request" ? (selected.leave_reason || "No reason provided.") : (selected.reason || "No reason provided.")}</Typography>
                  </Paper>
                </Box>
              </Box>

              <Box sx={{ mt: "auto", pt: 3 }}>
                {selected.type === "ot_request" && selected.status === "pending" && (
                  <TextField
                    label="Approved Hours"
                    type="number"
                    fullWidth
                    value={otApprovedHours}
                    onChange={(e) => setOtApprovedHours(e.target.value)}
                    sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    size="small"
                    inputProps={{ step: "0.5" }}
                  />
                )}

                {isSelectedOwnStep1Manual && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
                    Self-approval is not allowed at the line manager step.
                  </Alert>
                )}
                
                {selected.status === "pending" ? (
                  <Stack direction="row" spacing={2}>
                    <Button 
                      fullWidth 
                      variant="contained" 
                      color="success" 
                      startIcon={<ApproveIcon />}
                      disabled={isSelectedOwnStep1Manual}
                      onClick={() => handleAction(selected.id, selected.type, "approve", true)}
                      sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                    >
                      Approve
                    </Button>
                    <Button 
                      fullWidth 
                      variant="contained" 
                      color="error" 
                      startIcon={<RejectIcon />}
                      disabled={isSelectedOwnStep1Manual}
                      onClick={() => requiresRejectReason(selected.type) ? openRejectDialog(selected, true) : handleAction(selected.id, selected.type, "reject", true)}
                      sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                    >
                      Reject
                    </Button>
                  </Stack>
                ) : (
                  <Alert severity={selected.status === "approved" ? "success" : "error"} sx={{ borderRadius: 2, fontWeight: 600 }}>
                    This request has been {selected.status}.
                  </Alert>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Reject Reason Dialog */}
      <Dialog open={rejectDialog.open} onClose={closeRejectDialog} maxWidth="sm" fullWidth TransitionComponent={Fade}>
        <DialogTitle sx={{ fontWeight: 800 }}>Reject Request</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Rejection reason is mandatory. The staff member will see this reason in their request history.
            </Alert>
            {rejectDialog.item && (
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <Typography variant="body2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {rejectDialog.item.staff_name}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  {rejectDialog.item.type?.replaceAll("_", " ").toUpperCase()} | {formatDDMMYYYY(rejectDialog.item.date) || "-"}
                </Typography>
              </Box>
            )}
            <TextField
              label="Rejection Reason"
              value={rejectDialog.reason}
              onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
              multiline
              rows={4}
              required
              autoFocus
              fullWidth
              error={rejectDialog.open && rejectDialog.reason.trim().length === 0}
              helperText="Please enter a clear reason before rejecting."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeRejectDialog} sx={{ borderRadius: 2, fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<RejectIcon />}
            onClick={submitReject}
            disabled={!rejectDialog.reason.trim()}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Preview Dialog */}
      <Dialog open={photoOpen} onClose={handleClosePhoto} maxWidth="md" fullWidth TransitionComponent={Fade}>
        <DialogTitle sx={{ fontWeight: 800 }}>{photoTitle}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: "#f1f5f9" }}>
          {photoLoading ? (
            <CircularProgress color="primary" />
          ) : photoUrl ? (
            <Box
              component="img"
              src={photoUrl}
              alt="Attachment"
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 2, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
            />
          ) : (
            <Typography color="textSecondary">Document not found or failed to load.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {photoUrl && (
            <Button variant="outlined" sx={{ borderRadius: 2, fontWeight: 700 }} onClick={() => window.open(photoUrl, '_blank')}>
              Open Full Resolution
            </Button>
          )}
          <Button variant="contained" onClick={handleClosePhoto} sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#0f172a" }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snack.open} 
        autoHideDuration={4000} 
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Helper Components
const DetailRow = ({ label, value, highlight, color }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>{label}</Typography>
    <Typography variant="body2" sx={{ 
      fontWeight: 700, 
      color: color || (highlight ? "#ef4444" : "#1e293b"),
      fontSize: highlight ? "0.95rem" : "0.875rem"
    }}>
      {value}
    </Typography>
  </Stack>
);

const Avatar = ({ children, sx }) => (
  <Box sx={{ 
    width: 40, height: 40, borderRadius: "50%", 
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "white", ...sx 
  }}>
    {children}
  </Box>
);
